import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentUploadSection = ({ documents, handleFileUpload, handleFileRemove, errors }) => {
  const [dragActive, setDragActive] = useState({});

  const handleDrag = (e, docType) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(prev => ({ ...prev, [docType]: true }));
    } else if (e?.type === "dragleave") {
      setDragActive(prev => ({ ...prev, [docType]: false }));
    }
  };

  const handleDrop = (e, docType) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(prev => ({ ...prev, [docType]: false }));
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileUpload(e?.dataTransfer?.files?.[0], docType);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const documentTypes = [
    {
      type: 'resume',
      label: 'Resume/CV',
      required: false,
      description: 'PDF, DOC, DOCX (Max 5MB) - Optional',
      icon: 'FileText'
    },
    {
      type: 'coverLetter',
      label: 'Cover Letter',
      required: false,
      description: 'PDF, DOC, DOCX (Max 5MB)',
      icon: 'Mail'
    },
    {
      type: 'portfolio',
      label: 'Portfolio',
      required: false,
      description: 'PDF, ZIP (Max 10MB)',
      icon: 'Briefcase'
    },
    {
      type: 'certificates',
      label: 'Certificates',
      required: false,
      description: 'PDF (Max 5MB)',
      icon: 'Award'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="Upload" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Upload Documents</h3>
          <p className="text-sm text-muted-foreground">Attach your resume and supporting documents</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {documentTypes?.map((docType) => (
          <div key={docType?.type} className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Icon name={docType?.icon} size={16} />
                {docType?.label}
                {docType?.required && <span className="text-error">*</span>}
              </label>
            </div>

            {!documents?.[docType?.type] ? (
              <div
                onDragEnter={(e) => handleDrag(e, docType?.type)}
                onDragLeave={(e) => handleDrag(e, docType?.type)}
                onDragOver={(e) => handleDrag(e, docType?.type)}
                onDrop={(e) => handleDrop(e, docType?.type)}
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-smooth ${
                  dragActive?.[docType?.type]
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <input
                  type="file"
                  id={`file-${docType?.type}`}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={(e) => {
                    if (e?.target?.files && e?.target?.files?.[0]) {
                      handleFileUpload(e?.target?.files?.[0], docType?.type);
                    }
                  }}
                  accept=".pdf,.doc,.docx,.zip"
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center justify-center w-12 h-12 bg-muted rounded-full">
                    <Icon name="Upload" size={24} className="text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Drop file here or <span className="text-primary">browse</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{docType?.description}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg border border-border">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name="FileCheck" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {documents?.[docType?.type]?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(documents?.[docType?.type]?.size)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleFileRemove(docType?.type)}
                  iconName="X"
                  iconSize={18}
                >
                  Remove
                </Button>
              </div>
            )}

            {errors?.[docType?.type] && (
              <p className="text-sm text-error flex items-center gap-1">
                <Icon name="AlertCircle" size={14} />
                {errors?.[docType?.type]}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentUploadSection;